import React, { useState, useEffect } from "react";
import { queryContract } from "../../Components/functions/Contract";
import { getNibiruSDK, getContractAddresses } from "../../config/network";

const nibiru = getNibiruSDK();

// Helper function to get vault contract address based on network
const getVaultContractAddress = () => {
  const address = getContractAddresses().vault;
  if (!address) {
    throw new Error("Vault contract address not configured");
  }
  return address;
};

const WithdrawalTimeline = () => {
  const [timelineData, setTimelineData] = useState({
    currentEpoch: 0,
    timelock: 0,
    inActionWindow: false
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTimelineData = async () => {
      try {
        setLoading(true);
        
        // Execute all queries in parallel for better performance
        const vaultAddress = getVaultContractAddress();
        const [currentEpoch, timelock, epochStart] = await Promise.all([
          // Get current epoch
          queryContract(
            vaultAddress,
            { current_epoch: {} },
            nibiru.endptTm
          ),
          
          // Get timelock
          queryContract(
            vaultAddress,
            { withdraw_epochs_timelock: {} },
            nibiru.endptTm
          ),
          
          // Get epoch start time (CORRECTED QUERY)
          queryContract(
            vaultAddress,
            { get_current_epoch_start: {} },
            nibiru.endptTm
          )
        ]);
        
        // Calculate if in action window
        const epochStartTime = Number(epochStart) / 1000000; // Convert from nanoseconds to milliseconds
        const currentTime = Date.now();
        const actionWindowDuration = 48 * 60 * 60 * 1000; // 48 hours
        const inActionWindow = currentTime >= epochStartTime && 
                              currentTime <= (epochStartTime + actionWindowDuration);
        
        // Validate data before updating state
        if (currentEpoch && timelock) {
          setTimelineData({
            currentEpoch: Number(currentEpoch),
            timelock: Number(timelock),
            inActionWindow
          });
        } else {
          console.error("Received incomplete timeline data:", { currentEpoch, timelock });
        }
      } catch (error) {
        console.error("Error fetching timeline data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTimelineData();
  }, []);
  
  // Generate array of epochs for timeline
  const generateEpochs = () => {
    const { currentEpoch, timelock } = timelineData;
    const epochs = [];
    
    // Add current epoch
    epochs.push({
      epoch: currentEpoch,
      status: "current",
      label: "Current Epoch",
      description: timelineData.inActionWindow ? 
        "Withdrawal window open" : 
        "Withdrawal window closed"
    });
    
    // Add waiting epochs
    for (let i = 1; i < timelock; i++) {
      epochs.push({
        epoch: currentEpoch + i,
        status: "waiting",
        label: `Waiting Epoch ${i}`,
        description: "Waiting period"
      });
    }
    
    // Add redemption epoch
    epochs.push({
      epoch: currentEpoch + timelock,
      status: "redemption",
      label: "Redemption Epoch",
      description: "Funds available for withdrawal"
    });
    
    return epochs;
  };

  const epochs = !loading ? generateEpochs() : [];

  // The two branches below are keyed distinctly. Without that React reconciles
  // them by position and *reuses* the nodes — the spinner div becomes the
  // timeline's container — and App.css's global `transition: all !important`
  // then interpolates the spinner's 2px circle into a full-width white ellipse
  // on the way out.
  if (loading) {
    return (
      <div key="wd-loading" className="ce-vault-panel ce-wd-panel p-4 mb-6">
        <div className="ce-eyebrow mb-4">Withdrawal Timeline</div>
        <div className="flex items-center justify-center py-8">
          <div className="ce-vault-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div key="wd-loaded" className="ce-vault-panel ce-wd-panel p-5 mb-6 w-full overflow-hidden">
      <div className="ce-eyebrow mb-5">Withdrawal Timeline</div>

      <div className="timeline-container">
        <div className="relative">
          {/* The rail the nodes sit on. A recessed groove rather than a
              hairline: the node travelling along it is a raised disc, and a
              raised thing needs something sunken to sit in. A conveyor
              stripe rides on top so the wait itself reads as *live*. */}
          <div
            className="absolute top-5 left-0 right-0 md:block hidden overflow-hidden"
            style={{ height: 4, borderRadius: 999, background: "var(--surface-2)", boxShadow: "var(--inset-sm)" }}
          >
            <div className="ce-wd-rail-progress ce-wd-rail-fill w-full h-full" style={{ "--i": epochs.length }} />
          </div>

          {/* Timeline nodes */}
          <div className="relative">
            {/* Mobile: horizontally scrolled */}
            <div className="md:hidden flex gap-8 overflow-x-auto pb-2 px-4 focus:outline-none">
              {epochs.map((epoch, index) => (
                <div key={index} className="ce-wd-node flex flex-col items-center relative z-10 flex-shrink-0 min-w-[80px]" style={{ "--i": index }}>
                  {index < epochs.length - 1 && (
                    <div
                      className="absolute top-5 left-[40px] w-[40px] z-0 overflow-hidden"
                      style={{ height: 4, borderRadius: 999, background: "var(--surface-2)", boxShadow: "var(--inset-sm)" }}
                    >
                      <div className="ce-wd-rail-progress w-full h-full" />
                    </div>
                  )}
                  <EpochNode epoch={epoch} />
                  <div className="mt-2 text-sm font-medium text-center" style={{ color: "var(--ink)" }}>{epoch.label}</div>
                  <div className="text-xs max-w-20 text-center mt-1" style={{ color: "var(--ink-3)" }}>{epoch.description}</div>
                </div>
              ))}
            </div>

            {/* Desktop */}
            <div className="hidden md:flex justify-between relative">
              {epochs.map((epoch, index) => (
                <div key={index} className="ce-wd-node flex flex-col items-center relative z-10" style={{ "--i": index }}>
                  <EpochNode epoch={epoch} />
                  <div className="mt-2 text-sm font-medium text-center" style={{ color: "var(--ink)" }}>{epoch.label}</div>
                  <div className="text-xs max-w-32 text-center mt-1" style={{ color: "var(--ink-3)" }}>{epoch.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="ce-vault-well ce-wd-well-in mt-6 p-4" style={{ color: "var(--ink-3)", fontSize: 12, lineHeight: 1.5 }}>
        <p className="mb-2">• If you make a withdrawal request now, your funds will be available in epoch {timelineData.currentEpoch + timelineData.timelock}</p>
        <p>• You must redeem your funds during the first 48 hours of the redemption epoch</p>
      </div>
    </div>
  );
};

/* One node on the rail. Raised in all three states — it is an object on the
   groove — with the fill carrying which state it is in: accent for the epoch
   you are in, the up green for one that has landed, and the neutral raise for
   one still ahead. */
const EpochNode = ({ epoch }) => {
  const state =
    epoch.status === "current"
      ? { background: "linear-gradient(180deg, var(--cat-accent-lift, #7A4BFF) 0%, var(--cat-accent, #5B1DEE) 100%)", color: "#FFFFFF" }
      : epoch.status === "waiting"
        ? { backgroundImage: "var(--raise)", color: "var(--ink-3)" }
        : { background: "linear-gradient(180deg, #24A066 0%, #1F8B58 100%)", color: "#FFFFFF" };

  return (
    <div
      className="ce-figure flex items-center justify-center relative z-10 transition-transform duration-200 hover:scale-110"
      style={{
        width: 40,
        height: 40,
        borderRadius: 999,
        fontWeight: 600,
        fontSize: 14,
        boxShadow: "var(--pillow-sm)",
        ...state,
      }}
    >
      {epoch.epoch}
    </div>
  );
};

export default WithdrawalTimeline;